import type { AbuseScenario, Analysis } from '../types';
import { democraticBandLabel } from '../utils/democraticBand';

interface Props {
  item: Analysis;
  onClose: () => void;
}

const RISK_LABEL: Record<string, string> = {
  low: 'Nízké',
  medium: 'Střední',
  high: 'Vysoké',
};

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className="confidence-bar" aria-label={`Spolehlivost ${pct} %`}>
      <div className="confidence-bar__fill" style={{ width: `${pct}%` }} />
      <span className="confidence-bar__label">{pct} %</span>
    </div>
  );
}

function StringList({ items, label }: { items: string[]; label: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="detail-section">
      <h4 className="detail-section__title">{label}</h4>
      <ul className="detail-list">
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}

function formatAbuseScenario(scenario: string | AbuseScenario, index: number): { title: string; detail: string } {
  if (typeof scenario === 'string') {
    return { title: scenario, detail: '' };
  }
  return {
    title: scenario.scenario_title || `Scénář ${index + 1}`,
    detail: scenario.mechanism || scenario.trigger_conditions || scenario.textual_evidence || '',
  };
}

function AbuseScenarioList({ items, label }: { items: Array<string | AbuseScenario>; label: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="detail-section">
      <h4 className="detail-section__title">{label}</h4>
      <ul className="detail-list">
        {items.map((rawScenario, i) => {
          const scenario = formatAbuseScenario(rawScenario, i);
          return (
            <li key={`${scenario.title}-${i}`}>
              <strong>{scenario.title}</strong>
              {scenario.detail && <p>{scenario.detail}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function DetailPanel({ item, onClose }: Props) {
  const fd = item.final_decision;
  const isPlaceholder = fd && fd.alert !== 'yes' && fd.alert !== 'no';

  return (
    <aside className="detail-panel" role="complementary" aria-label="Detail analýzy">
      <button className="detail-panel__close" onClick={onClose} aria-label="Zavřít detail">
        ✕
      </button>

      <div className="detail-panel__body">
        <div className="detail-panel__badges">
          <span className={`risk-badge risk-badge--${item.risk}`}>
            {RISK_LABEL[item.risk] ?? item.risk}
          </span>
          {!item.analysis_complete && (
            <span className="badge badge--incomplete">Rozpracováno</span>
          )}
          {fd && !isPlaceholder && fd.alert === 'yes' && (
            <span className="badge badge--alert">ALERT</span>
          )}
          {fd && !isPlaceholder && fd.alert === 'no' && (
            <span className="badge badge--ok">Bez alertu</span>
          )}
          {fd && isPlaceholder && (
            <span className="badge badge--placeholder">Placeholder výstup</span>
          )}
        </div>

        <h2 className="detail-panel__title">{item.title}</h2>

        <dl className="detail-meta">
          {item.area && <><dt>Oblast</dt><dd>{item.area}</dd></>}
          {item.owner && <><dt>Zpracovatel</dt><dd>{item.owner}</dd></>}
          {item.deadline && item.deadline !== 'Bez terminu' && (
            <><dt>Termín</dt><dd>{item.deadline}</dd></>
          )}
          {item.updated_at && (
            <><dt>Aktualizováno</dt><dd>{new Date(item.updated_at).toLocaleString('cs-CZ')}</dd></>
          )}
        </dl>

        {item.impact && (
          <div className="detail-section">
            <h4 className="detail-section__title">Dopad</h4>
            <p>{item.impact}</p>
          </div>
        )}

        {item.summary && (
          <div className="detail-section">
            <h4 className="detail-section__title">Shrnutí</h4>
            <p>{item.summary}</p>
          </div>
        )}

        {item.detail && (
          <div className="detail-section">
            <h4 className="detail-section__title">Detail</h4>
            <p>{item.detail}</p>
          </div>
        )}

        {item.action && (
          <div className="detail-section">
            <h4 className="detail-section__title">Doporučená akce</h4>
            <p>{item.action}</p>
          </div>
        )}

        {fd && !isPlaceholder && (
          <div className="detail-final-decision">
            <h3 className="detail-final-decision__heading">Výsledek analýzy</h3>

            {fd.plain_explanation && (
              <p className="detail-final-decision__explanation">{fd.plain_explanation}</p>
            )}

            {fd.summary && fd.summary !== fd.plain_explanation && (
              <div className="detail-section">
                <h4 className="detail-section__title">Souhrn závěru</h4>
                <p>{fd.summary}</p>
              </div>
            )}

            <dl className="detail-meta">
              {fd.final_risk_level && (
                <><dt>Finální riziko</dt><dd>{RISK_LABEL[fd.final_risk_level] ?? fd.final_risk_level}</dd></>
              )}
              {fd.constitutional_relevance && (
                <><dt>Ústavní relevance</dt><dd>{RISK_LABEL[fd.constitutional_relevance] ?? fd.constitutional_relevance}</dd></>
              )}
              {typeof fd.final_score === 'number' && (
                <><dt>Skóre</dt><dd>{fd.final_score} <span className="democratic-band-chip">{democraticBandLabel(fd.final_score)}</span></dd></>
              )}
            </dl>

            {typeof fd.confidence === 'number' && (
              <div className="detail-section">
                <h4 className="detail-section__title">Spolehlivost</h4>
                <ConfidenceBar value={fd.confidence} />
              </div>
            )}

            <StringList items={fd.reasons} label="Důvody" />
            <StringList items={fd.democratic_impact_areas} label="Dopad na demokratické oblasti" />
            <StringList items={fd.key_mechanisms} label="Klíčové mechanismy" />
            <AbuseScenarioList items={fd.abuse_scenarios} label="Scenare a slaba mista k overeni" />
          </div>
        )}

        {item.detail_url && (
          <div className="detail-panel__link">
            <a href={item.detail_url} target="_blank" rel="noopener noreferrer">
              Zobrazit na PSP.cz ↗
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
